import { decodeUpgradeToFunctionData, encodedUpgradeToAndCall } from '../operations';
import { encodeFunctionData, isAddress, isHex } from 'viem';
import { modularAccountAbi } from './permissionless/accounts/modular/abis/modularAccountAbi';
export async function getCallDataForUserop(args) {
    const { callData, smartAccount, signer, toAddress, value, needsUpgrade, functionHash } = args;
    if (!smartAccount.client.account) {
        throw new Error('smartAccount.client.account is required');
    }
    const account = smartAccount.client.account;
    if (Array.isArray(toAddress)) {
        if (!Array.isArray(callData)) {
            throw new Error('callData must be an array if toAddress is an array');
        }
        if (!Array.isArray(value)) {
            throw new Error('value must be an array if toAddress is an array');
        }
        if (toAddress.length !== callData.length) {
            throw new Error('toAddress and callData must be the same length');
        }
        if (toAddress.length !== value.length) {
            throw new Error('toAddress and value must be the same length');
        }
        if (!toAddress.every((to) => isAddress(to))) {
            throw new Error('toAddress must be an array of addresses');
        }
        if (!callData.every((callData) => isHex(callData))) {
            throw new Error('callData must be an array of calldata');
        }
        // installing a subscription uses installValidation, which can be called directly, or via executeBatch
        if (functionHash === 'installSubscription' ||
            functionHash === 'uninstallSubscription' ||
            functionHash === 'installAccountModules') {
            if (smartAccount.type === 'simple') {
                throw new Error('installSubscription, uninstallSubscription, or installAccountModules is not supported for simple accounts');
            }
            // manually encode executeBatch because the way it's encoded in toModularSmartAccount.encodeCalls (viem compliant signature for encodeCalls), if it's only a single call (which in this instance it is), it uses 'execute'
            // but 'execute' doesn't work with installValidation, it has to be encoded as executeBatch
            const calls = toAddress.map((to, i) => ({
                target: to,
                data: callData[i],
                value: value[i],
            }));
            // modular accounts have special encoding sometimes, see toModularSmartAccount, which perform checks on validation data.
            // we shouldn't need that here, we can just encode the calls using the ABI. Otherwise we need to expose the toModularSmartAccount functions in TS (encodeCalldata), it's just easiser to conform to viem/permissionless types, so not doing that for now
            const encodedCalls = encodeFunctionData({
                abi: modularAccountAbi,
                functionName: 'executeBatch',
                args: [calls],
            });
            return encodedCalls;
        }
        // we can't encode executeBatch with value on simple accounts - see UserOperation.sendUserOp, where the account is upgraded before performing the rest of the op
        if (needsUpgrade && (value.length === 0 || value.every((v) => v === 0n))) {
            const { toAddress: upgradeToAddress, callData: upgradeToCallData } = await encodedUpgradeToAndCall({
                chain: smartAccount.client.chain,
                smartAccountType: smartAccount.type,
                smartAccountAddress: smartAccount.address,
                signer,
            });
            const calls = [
                {
                    to: upgradeToAddress,
                    data: upgradeToCallData,
                },
                ...toAddress.map((to, i) => ({
                    to,
                    data: callData[i],
                })),
            ];
            // encode executeBatch with simple account
            return account.encodeCalls(calls);
        }
        const calls = toAddress.map((to, i) => ({
            to,
            data: callData[i],
            value: value[i],
        }));
        return account.encodeCalls(calls);
    }
    else {
        if (Array.isArray(callData)) {
            throw new Error('callData must be a string if toAddress is a string');
        }
        if (Array.isArray(value)) {
            throw new Error('value must be a bigint if toAddress is a string');
        }
        if (Array.isArray(toAddress)) {
            throw new Error('toAddress must be a string if callData is a string');
        }
        if (!isAddress(toAddress)) {
            throw new Error('toAddress must be an address');
        }
        if (!isHex(callData)) {
            throw new Error('callData must be a hex string');
        }
        // installing a subscription uses installValidation, which can be called directly, or via executeBatch, but not via execute
        if (functionHash === 'installSubscription' ||
            functionHash === 'uninstallSubscription' ||
            functionHash === 'installAccountModules') {
            throw new Error('installSubscription, uninstallSubscription, or installAccountModules is not supported for single execute');
        }
        /**
         * IMPORTANT: This value can result in RPC errors if the smart account has insufficient funds
         *
         * If estimating user operation gas, you can override sender balance via state overrides https://docs.stackup.sh/docs/erc-4337-bundler-rpc-methods#eth_senduseroperation
         * which we are doing, see prompttUser middleware
         *
         * However, in the case of a tx that costs ETH, but that we also want to sponsor, this value should be 0
         * Otherwise, the paymaster will reject the operation if the user does not have enough funds
         * This kind of tx would be something like joining a town that has a fixed membership cost, but ALSO contains prepaid seats
         */
        // if needs upgrade, change format to executeBatch
        if (needsUpgrade && (!value || value === 0n)) {
            if (decodeUpgradeToFunctionData({ data: callData }) !== 'upgradeToAndCall') {
                const { toAddress: upgradeToAddress, callData: upgradeToCallData } = await encodedUpgradeToAndCall({
                    chain: smartAccount.client.chain,
                    smartAccountType: smartAccount.type,
                    smartAccountAddress: smartAccount.address,
                    signer,
                });
                const calls = [
                    {
                        to: upgradeToAddress,
                        data: upgradeToCallData,
                    },
                    {
                        to: toAddress,
                        data: callData,
                    },
                ];
                // encode executeBatch with simple account
                return account.encodeCalls(calls);
            }
        }
        return account.encodeCalls([
            {
                to: toAddress,
                data: callData,
                value: value ?? 0n,
            },
        ]);
    }
}
